import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  UserProfile,
  RANK_TIERS,
  getExpProgress,
  saveUserProfile,
  generateSyncPin,
} from '../../core/profile';
import {
  syncProfileToCloud,
  loadProfileFromCloud,
  importProfileByPin,
  getLastSyncedAt,
} from '../../core/cloudSync';
import {
  User,
  Trophy,
  Award,
  BarChart3,
  Cloud,
  Check,
  Copy,
  Edit2,
  X,
  Sparkles,
  Zap,
  Flame,
  Shield,
  RefreshCw,
  QrCode,
  Globe,
  Mail,
  Smartphone,
  ExternalLink,
  LogOut,
  Crown,
  UploadCloud,
  DownloadCloud,
} from 'lucide-react';

interface Props {
  profile: UserProfile;
  onUpdateProfile: (updated: UserProfile) => void;
  onClose: () => void;
}

export const PlayerProfileModal: React.FC<Props> = ({ profile, onUpdateProfile, onClose }) => {
  const { user, isAuthenticated, loginWithRedirect, logout, getIdTokenClaims } = useAuth0();

  const role = ((user?.['https://weiai.ai/role'] as string) || 'user').toLowerCase();
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || isOwner;

  const [activeTab, setActiveTab] = useState<'overview' | 'ladder' | 'achieve' | 'sync' | 'stats' | 'achievements'>('stats');
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [nameInput, setNameInput] = useState<string>(profile.nickname);
  const [copiedPin, setCopiedPin] = useState<boolean>(false);
  const [syncPinInput, setSyncPinInput] = useState<string>('');
  const [syncMessage, setSyncMessage] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncedTime, setLastSyncedTime] = useState<string | null>(getLastSyncedAt());
  const [showWechatModal, setShowWechatModal] = useState<boolean>(false);
  const [wechatNicknameInput, setWechatNicknameInput] = useState<string>(profile.wechatNickname || '');
  const [isBindingWechat, setIsBindingWechat] = useState<boolean>(false);

  const expProg = getExpProgress(profile.exp);
  const winRate =
    profile.stats.totalGames > 0
      ? Math.round((profile.stats.wins / profile.stats.totalGames) * 100)
      : 0;

  // Sync real Auth0 identity into profile and auto-sync cloud profile
  useEffect(() => {
    let isMounted = true;
    if (isAuthenticated && user) {
      const provider = user.sub?.startsWith('google')
        ? 'google'
        : user.sub?.startsWith('wechat')
        ? 'wechat'
        : 'email';

      const realName = user.nickname || user.name;
      const baseUpdated: UserProfile = {
        ...profile,
        nickname: realName || profile.nickname,
        avatarUrl: user.picture || profile.avatarUrl,
        accountType: provider as any,
      };

      // Perform background auto-sync to merge cloud profile
      (async () => {
        try {
          const claims = await getIdTokenClaims();
          const token = claims?.__raw;
          const res = await syncProfileToCloud(baseUpdated, token, user.sub);
          if (isMounted && res.success && res.profile) {
            onUpdateProfile(res.profile);
            setLastSyncedTime(res.syncedAt || new Date().toISOString());
          } else if (isMounted) {
            saveUserProfile(baseUpdated);
            onUpdateProfile(baseUpdated);
          }
        } catch (e) {
          if (isMounted) {
            saveUserProfile(baseUpdated);
            onUpdateProfile(baseUpdated);
          }
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.sub]);

  const handleSaveNickname = () => {
    if (nameInput.trim()) {
      const updated = { ...profile, nickname: nameInput.trim() };
      saveUserProfile(updated);
      onUpdateProfile(updated);
      setIsEditingName(false);
      // Auto save to cloud if authenticated
      if (isAuthenticated && user?.sub) {
        syncProfileToCloud(updated, undefined, user.sub);
      }
    }
  };

  const handleCopyPin = () => {
    navigator.clipboard.writeText(profile.syncPin);
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  // Real Cross-Device 6-Digit PIN import
  const handleApplySyncPin = async () => {
    const pin = syncPinInput.trim();
    if (pin.length !== 6) {
      setSyncMessage('⚠️ 请输入正确的 6 位数字同步码');
      return;
    }
    setIsSyncing(true);
    setSyncMessage('🔄 正在校验 6 位同步码并拉取云端档案...');
    try {
      const res = await importProfileByPin(pin);
      if (res.success && res.profile) {
        onUpdateProfile(res.profile);
        setLastSyncedTime(res.syncedAt || new Date().toISOString());
        setSyncMessage(
          `✅ 跨设备同步成功！已成功载入「${res.profile.nickname}」(Lv.${res.profile.level}，EXP: ${res.profile.exp}) 的云端战绩档案！`
        );
        setSyncPinInput('');
      } else {
        setSyncMessage(`❌ ${res.error || '未找到该同步码对应的云端战绩'}`);
      }
    } catch (e: any) {
      setSyncMessage(`❌ 同步失败: ${e.message || '网络连接异常'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Manual Trigger: Cloud Push & Smart Merge
  const handleManualSync = async () => {
    setIsSyncing(true);
    setSyncMessage('🔄 正在与唯爱云端同步战绩数据...');
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      const res = await syncProfileToCloud(profile, token, user?.sub);
      if (res.success && res.profile) {
        onUpdateProfile(res.profile);
        setLastSyncedTime(res.syncedAt || new Date().toISOString());
        setSyncMessage(
          `✅ 云端同步成功！当前段位经验 EXP: ${res.profile.exp} (Lv.${res.profile.level}) 已安全保存在云端。`
        );
      } else {
        setSyncMessage(`❌ 同步失败: ${res.error || '未知错误'}`);
      }
    } catch (e: any) {
      setSyncMessage(`❌ 同步失败: ${e.message || '网络连接异常'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Manual Trigger: Pull latest from Cloud
  const handleRestoreCloud = async () => {
    if (!isAuthenticated || !user?.sub) {
      setSyncMessage('⚠️ 请先绑定并登录唯爱账号，即可从云端读取历史战绩');
      return;
    }
    setIsSyncing(true);
    setSyncMessage('🔄 正在从云端读取您的历史战绩档案...');
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      const res = await loadProfileFromCloud(token, user.sub);
      if (res.success && res.profile) {
        onUpdateProfile(res.profile);
        setLastSyncedTime(res.syncedAt || new Date().toISOString());
        setSyncMessage(`✅ 已从云端成功恢复档案！「${res.profile.nickname}」(Lv.${res.profile.level})`);
      } else {
        setSyncMessage(`ℹ️ ${res.error || '云端暂无历史存档，请先点击「立即同步」上传当前战绩'}`);
      }
    } catch (e: any) {
      setSyncMessage(`❌ 恢复失败: ${e.message || '网络连接异常'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleBindWechat = async () => {
    if (!wechatNicknameInput.trim()) {
      setSyncMessage('⚠️ 请输入您的微信昵称或微信号');
      return;
    }
    setIsBindingWechat(true);
    try {
      const updated: UserProfile = {
        ...profile,
        wechatNickname: wechatNicknameInput.trim(),
        wechatBoundAt: new Date().toISOString(),
      };
      saveUserProfile(updated);
      onUpdateProfile(updated);

      // Persist to cloud if authenticated or via PIN
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      await syncProfileToCloud(updated, token, user?.sub);

      setShowWechatModal(false);
      setSyncMessage(`🎉 成功关联微信账号「${wechatNicknameInput.trim()}」！战绩与微信已永久互通。`);
      setTimeout(() => setSyncMessage(''), 4000);
    } catch (e: any) {
      setSyncMessage(`❌ 微信关联失败: ${e.message || '网络连接异常'}`);
    } finally {
      setIsBindingWechat(false);
    }
  };

  const handleRealLogin = (connection?: string) => {
    if (connection) {
      loginWithRedirect({
        authorizationParams: {
          connection,
          redirect_uri: window.location.origin,
        },
      });
    } else {
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      });
    }
  };

  const displayNickname = (isAuthenticated && user?.nickname) || profile.nickname;
  const avatarUrl = (isAuthenticated && user?.picture) || profile.avatarUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/85 backdrop-blur-md animate-fade-in font-sans text-slate-100">
      <div className="bg-slate-900 border-2 border-amber-500/40 rounded-3xl max-w-2xl w-full p-4 sm:p-6 shadow-2xl space-y-4 max-h-[92vh] flex flex-col text-left">
        {/* Header: User Profile Card */}
        <div className="shrink-0 bg-slate-950 p-4 rounded-2xl border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 text-slate-400 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Avatar & Name & Tier */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-tr from-amber-500 via-rose-500 to-sky-500 p-0.5 shadow-lg flex items-center justify-center">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayNickname}
                    className="w-full h-full rounded-[14px] object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-900 rounded-[14px] flex items-center justify-center text-2xl">
                    {expProg.tier.icon}
                  </div>
                )}
              </div>
              {isOwner && (
                <span className="absolute -top-1.5 -right-1.5 text-xs drop-shadow" title="最高所有者">
                  👑
                </span>
              )}
            </div>

            <div className="space-y-1">
              {isEditingName ? (
                <div className="flex items-center space-x-1.5">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    maxLength={16}
                    className="bg-slate-800 border border-amber-500 text-xs px-2 py-1 rounded text-white focus:outline-none"
                  />
                  <button
                    onClick={handleSaveNickname}
                    className="bg-amber-500 text-slate-950 px-2 py-1 rounded text-xs font-bold"
                  >
                    保存
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h3 className="text-base sm:text-lg font-black text-white">{displayNickname}</h3>
                  {!isAuthenticated && (
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="text-slate-400 hover:text-amber-400"
                      title="修改本地昵称"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${expProg.tier.badgeColor}`}>
                  {expProg.tier.icon} {expProg.tier.name}
                </span>
                <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full font-mono font-bold">
                  Lv.{profile.level}
                </span>
                <span className="text-[10px] bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded-full font-bold border border-amber-500/30">
                  {isAuthenticated && user
                    ? isOwner
                      ? '👑 OWNER'
                      : isAdmin
                      ? '🛡️ ADMIN'
                      : '正式会员'
                    : '游客账号'}
                </span>
              </div>
            </div>
          </div>

          {/* Level EXP Progress Bar */}
          <div className="w-full sm:w-56 space-y-1 bg-slate-900/80 p-2.5 rounded-xl border border-slate-800">
            <div className="flex items-center justify-between text-[11px] font-bold">
              <span className="text-amber-400">EXP 经验值</span>
              <span className="text-slate-300 font-mono">
                {profile.exp} / {expProg.tier.maxExp}
              </span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-rose-500 transition-all duration-500 rounded-full"
                style={{ width: `${expProg.percent}%` }}
              ></div>
            </div>
            <div className="text-[9px] text-slate-400 text-right">
              {expProg.nextTier ? `距离【${expProg.nextTier.name}】还差 ${expProg.nextTier.minExp - profile.exp} EXP` : '已达最高宗师段位'}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="shrink-0 flex items-center space-x-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-black flex items-center justify-center space-x-1.5 transition-all ${
              activeTab === 'stats'
                ? 'bg-amber-500 text-slate-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            <span>战绩雷达</span>
          </button>

          <button
            onClick={() => setActiveTab('ladder')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-black flex items-center justify-center space-x-1.5 transition-all ${
              activeTab === 'ladder'
                ? 'bg-amber-500 text-slate-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Trophy className="w-3.5 h-3.5" />
            <span>段位阶梯</span>
          </button>

          <button
            onClick={() => setActiveTab('achievements')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-black flex items-center justify-center space-x-1.5 transition-all ${
              activeTab === 'achievements'
                ? 'bg-amber-500 text-slate-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Award className="w-3.5 h-3.5" />
            <span>荣誉勋章</span>
          </button>

          <button
            onClick={() => setActiveTab('sync')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-black flex items-center justify-center space-x-1.5 transition-all ${
              activeTab === 'sync'
                ? 'bg-gradient-to-r from-emerald-500 to-sky-500 text-slate-950 shadow-md'
                : 'text-emerald-400 hover:text-white'
            }`}
          >
            <Cloud className="w-3.5 h-3.5" />
            <span>账号与云同步</span>
          </button>
        </div>

        {/* Tab 1: Combat Stats & Radar */}
        {activeTab === 'stats' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 text-center space-y-1">
                <div className="text-[11px] text-slate-400 font-bold">总对局数</div>
                <div className="text-xl sm:text-2xl font-black text-white font-mono">
                  {profile.stats.totalGames}
                </div>
              </div>

              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 text-center space-y-1">
                <div className="text-[11px] text-slate-400 font-bold">胜率 (Win Rate)</div>
                <div className="text-xl sm:text-2xl font-black text-emerald-400 font-mono">
                  {winRate}%
                </div>
              </div>

              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 text-center space-y-1">
                <div className="text-[11px] text-slate-400 font-bold">头游榜首 (MVP)</div>
                <div className="text-xl sm:text-2xl font-black text-amber-400 font-mono">
                  {profile.stats.topRankCount}
                </div>
              </div>

              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 text-center space-y-1">
                <div className="text-[11px] text-slate-400 font-bold">残局破解星数</div>
                <div className="text-xl sm:text-2xl font-black text-purple-400 font-mono">
                  {profile.stats.puzzlesSolved}
                </div>
              </div>
            </div>

            {/* Tactical Highlights */}
            <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-2">
              <div className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <span>实战战术高光数据</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-slate-900 p-2 rounded-xl">
                  <div className="text-[10px] text-slate-400">双贡压制次数</div>
                  <div className="font-bold text-amber-300 font-mono">{profile.stats.doubleTributesGiven}</div>
                </div>
                <div className="bg-slate-900 p-2 rounded-xl">
                  <div className="text-[10px] text-slate-400">复式绝境逆转</div>
                  <div className="font-bold text-rose-400 font-mono">{profile.stats.swapRematchWins}</div>
                </div>
                <div className="bg-slate-900 p-2 rounded-xl">
                  <div className="text-[10px] text-slate-400">打出炸弹总计</div>
                  <div className="font-bold text-sky-400 font-mono">{profile.stats.bombsPlayed}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Rank Tier Hierarchy */}
        {activeTab === 'ladder' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2.5 pr-1">
            {RANK_TIERS.map((tier) => {
              const isCurrent = expProg.tier.tier === tier.tier;
              const isPassed = profile.exp >= tier.maxExp && tier.maxExp !== Infinity;

              return (
                <div
                  key={tier.tier}
                  className={`p-3 rounded-2xl border transition-all flex items-center justify-between ${
                    isCurrent
                      ? 'bg-amber-500/15 border-amber-500/60 shadow-lg'
                      : isPassed
                      ? 'bg-slate-950/60 border-slate-800/80 opacity-75'
                      : 'bg-slate-950/30 border-slate-800/40 opacity-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-xl shadow">
                      {tier.icon}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-white text-xs">{tier.name}</span>
                        {isCurrent && (
                          <span className="text-[9px] bg-amber-500 text-slate-950 font-black px-1.5 py-0.2 rounded-full">
                            当前段位
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        EXP 范围: {tier.minExp} - {tier.maxExp === Infinity ? '无上限' : tier.maxExp}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    {isPassed ? (
                      <span className="text-[10px] text-emerald-400 font-bold flex items-center space-x-1">
                        <Check className="w-3 h-3" />
                        <span>已达成</span>
                      </span>
                    ) : isCurrent ? (
                      <span className="text-[10px] text-amber-400 font-bold font-mono">
                        {expProg.percent}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-600">未解锁</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tab 3: Achievements */}
        {activeTab === 'achievements' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {profile.achievements.map((ach) => (
                <div
                  key={ach.id}
                  className={`p-3 rounded-2xl border flex items-start space-x-3 ${
                    ach.unlocked
                      ? 'bg-slate-950 border-amber-500/40 shadow'
                      : 'bg-slate-950/40 border-slate-800/40 opacity-50'
                  }`}
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center text-lg shrink-0">
                    {ach.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-white truncate">{ach.name}</span>
                      {ach.unlocked && (
                        <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.2 rounded font-bold shrink-0">
                          +{ach.expReward} EXP
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{ach.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Cloud Sync & Real Account Authentication */}
        {activeTab === 'sync' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3.5 pr-1 text-xs">
            {syncMessage && (
              <div className="p-3 bg-amber-500/20 border border-amber-500/50 text-amber-300 rounded-xl font-bold animate-fade-in flex items-center justify-between">
                <span>{syncMessage}</span>
                <button
                  onClick={() => setSyncMessage('')}
                  className="text-amber-400 hover:text-white ml-2 text-xs"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Cloud Sync Status & Action Control */}
            <div className="bg-slate-950 p-4 rounded-2xl border border-sky-500/40 space-y-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-400">
                    <Cloud className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-black text-white text-xs flex items-center gap-1.5">
                      <span>唯爱云端战绩同步中心</span>
                      <span className="text-[9px] bg-sky-500/20 text-sky-300 px-1.5 py-0.2 rounded border border-sky-400/30">
                        实时双向合并
                      </span>
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {lastSyncedTime ? (
                        <span className="text-emerald-400 font-mono">
                          🟢 已同步 (上次: {new Date(lastSyncedTime).toLocaleTimeString()})
                        </span>
                      ) : (
                        <span className="text-amber-400/90 font-medium">🟡 本地档案尚未同步至云端</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-xs font-mono font-black text-amber-300">
                    EXP: {profile.exp}
                  </span>
                  <span className="text-[10px] text-slate-400 block">
                    {expProg.tier.name} Lv.{profile.level}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  disabled={isSyncing}
                  onClick={handleManualSync}
                  className="py-2.5 px-3 rounded-xl bg-gradient-to-r from-sky-500/20 to-emerald-500/20 hover:from-sky-500/30 hover:to-emerald-500/30 text-emerald-300 border border-emerald-500/40 font-black text-xs flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-50"
                >
                  <UploadCloud className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                  <span>{isSyncing ? '正在同步数据...' : '☁️ 立即同步至云端'}</span>
                </button>

                <button
                  type="button"
                  disabled={isSyncing}
                  onClick={handleRestoreCloud}
                  className="py-2.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 font-bold text-xs flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-50"
                >
                  <DownloadCloud className={`w-4 h-4 ${isSyncing ? 'animate-pulse' : ''}`} />
                  <span>📥 从云端恢复档案</span>
                </button>
              </div>
            </div>

            {/* 6-Digit Device Sync PIN */}
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-black text-white flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-emerald-400" />
                    <span>跨设备免密 6位同步码 (Sync PIN)</span>
                  </h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    在手机或另一台电脑输入此码，一键瞬间同步战绩、段位与 EXP。
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="font-mono text-lg font-black text-emerald-400 bg-slate-900 px-3 py-1 rounded-xl border border-slate-700 tracking-widest">
                    {profile.syncPin}
                  </span>
                  <button
                    onClick={handleCopyPin}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-transform active:scale-95"
                    title="复制同步码"
                  >
                    {copiedPin ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Input for importing another device's PIN */}
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="text"
                  maxLength={6}
                  placeholder="输入其他设备上的 6 位同步码..."
                  value={syncPinInput}
                  onChange={(e) => setSyncPinInput(e.target.value.replace(/\D/g, ''))}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono tracking-wider"
                />
                <button
                  disabled={isSyncing}
                  onClick={handleApplySyncPin}
                  className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black px-4 py-1.5 rounded-xl shadow transition-transform active:scale-95 shrink-0 disabled:opacity-50"
                >
                  {isSyncing ? '验证中...' : '导入同步'}
                </button>
              </div>
            </div>

            {/* Real Auth0 Account & Multi-Platform Linking */}
            {isAuthenticated && user ? (
              <div className="space-y-3">
                {/* Active Unified Account Header */}
                <div className="bg-slate-950 p-4 rounded-2xl border border-emerald-500/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center text-emerald-400">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-black text-white text-xs">已成功绑定唯爱统一账号</h4>
                        <p className="text-[11px] text-slate-400">{user.email || user.nickname || '已认证'}</p>
                      </div>
                    </div>
                    <div>
                      {isOwner ? (
                        <span className="text-[8px] font-black text-amber-300 bg-amber-400/20 border border-amber-400/40 px-2 py-0.5 rounded-full uppercase">
                          👑 OWNER
                        </span>
                      ) : isAdmin ? (
                        <span className="text-[8px] font-bold text-purple-300 bg-purple-400/20 border border-purple-400/40 px-2 py-0.5 rounded-full uppercase">
                          🛡️ ADMIN
                        </span>
                      ) : (
                        <span className="text-[8px] font-bold text-cyan-300 bg-cyan-400/20 border border-cyan-400/40 px-2 py-0.5 rounded-full uppercase">
                          正式会员
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-[11px] text-slate-300 leading-relaxed bg-slate-900/80 p-3 rounded-xl border border-slate-800 space-y-1">
                    <p>⚡ 您的段位荣誉、残局星数与三界功勋榜已与唯爱云端账户永久绑定。</p>
                    <p className="text-slate-500 text-[10px] font-mono">
                      主体凭证: {user.sub}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <a
                      href="https://account.weiai.ai"
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 py-2 px-3 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-400/40 text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    >
                      <span>管理个人中心 (account.weiai.ai)</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      className="py-2 px-4 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span>退出登录</span>
                    </button>
                  </div>
                </div>

                {/* Multi-Platform Account Linking (Always Accessible) */}
                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-black text-white text-xs flex items-center gap-1.5">
                      <Cloud className="w-4 h-4 text-emerald-400" />
                      <span>多平台账号快捷关联 (微信 / Google / 独立邮箱)</span>
                    </h4>
                    <span className="text-[10px] text-slate-400">一键互联·多端漫游</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {/* WeChat Button / Status */}
                    <div className={`p-3 rounded-xl border flex flex-col justify-between ${profile.wechatNickname ? 'bg-emerald-950/40 border-emerald-500/50' : 'bg-slate-900 border-slate-800'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-400 text-xs">
                          <QrCode className="w-3.5 h-3.5" />
                          <span>微信 (WeChat)</span>
                        </div>
                        {profile.wechatNickname ? (
                          <span className="text-[9px] text-emerald-300 bg-emerald-500/20 px-1.5 py-0.2 rounded font-mono">已绑定</span>
                        ) : (
                          <span className="text-[9px] text-amber-400/90 bg-amber-500/10 px-1.5 py-0.2 rounded font-bold">未关联</span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mb-2 truncate">
                        {profile.wechatNickname ? `微信号: ${profile.wechatNickname}` : '支持微信扫码与牌友圈互联'}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowWechatModal(true)}
                        className="w-full py-2 px-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 font-bold text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow"
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        <span>{profile.wechatNickname ? '修改微信关联 ↗' : '微信快捷关联 ↗'}</span>
                      </button>
                    </div>

                    {/* Google Status */}
                    <div className="p-3 rounded-xl border bg-slate-900 border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 font-bold text-rose-400 text-xs">
                          <Globe className="w-3.5 h-3.5" />
                          <span>Google 账号</span>
                        </div>
                        {user.sub?.startsWith('google') ? (
                          <span className="text-[9px] text-emerald-300 bg-emerald-500/20 px-1.5 py-0.2 rounded font-mono">已认证</span>
                        ) : (
                          <span className="text-[9px] text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded">未关联</span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mb-2 truncate">
                        {user.email || 'Google OAuth 免密'}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRealLogin('google-oauth2')}
                        className="w-full py-2 px-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-bold text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95"
                      >
                        <Globe className="w-3.5 h-3.5 text-rose-400" />
                        <span>{user.sub?.startsWith('google') ? '主凭证已绑定' : '关联 Google ↗'}</span>
                      </button>
                    </div>

                    {/* Email Status */}
                    <div className="p-3 rounded-xl border bg-slate-900 border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 font-bold text-sky-400 text-xs">
                          <Mail className="w-3.5 h-3.5" />
                          <span>独立邮箱</span>
                        </div>
                        {user.email ? (
                          <span className="text-[9px] text-emerald-300 bg-emerald-500/20 px-1.5 py-0.2 rounded font-mono">已激活</span>
                        ) : (
                          <span className="text-[9px] text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded">未关联</span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mb-2 truncate">
                        {user.email || '邮箱验证码与密码'}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRealLogin()}
                        className="w-full py-2 px-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-bold text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95"
                      >
                        <Mail className="w-3.5 h-3.5 text-sky-400" />
                        <span>{user.email ? '重新认证 ↗' : '绑定邮箱 ↗'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-black text-white flex items-center gap-1.5">
                    <Cloud className="w-4 h-4 text-sky-400" />
                    <span>一键绑定统一账号 (永久云端存档)</span>
                  </h4>
                  <span className="text-[10px] text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full font-bold">
                    当前为游客模式
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  绑定微信、Google 或独立邮箱后，段位战绩将永久保存在唯爱云端，并在多设备间实时同步，正式解锁三界功勋榜排位。
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowWechatModal(true)}
                    className="bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 border border-emerald-500/40 p-2.5 rounded-xl flex items-center justify-center space-x-2 font-bold transition-transform active:scale-95 shadow"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>微信快捷关联 ↗</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRealLogin('google-oauth2')}
                    className="bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 p-2.5 rounded-xl flex items-center justify-center space-x-2 font-bold transition-transform active:scale-95 shadow"
                  >
                    <Globe className="w-4 h-4 text-rose-400" />
                    <span>Google 登录 ↗</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRealLogin()}
                    className="bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 p-2.5 rounded-xl flex items-center justify-center space-x-2 font-bold transition-transform active:scale-95 shadow"
                  >
                    <Mail className="w-4 h-4 text-sky-400" />
                    <span>邮箱验证码登录 ↗</span>
                  </button>
                </div>
              </div>
            )}

            {/* Dedicated WeChat QR & Binding Modal */}
            {showWechatModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
                <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl max-w-sm w-full p-5 shadow-2xl space-y-4 relative">
                  <button
                    onClick={() => setShowWechatModal(false)}
                    className="absolute top-4 right-4 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
                      <QrCode className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white">微信快捷关联</h3>
                      <p className="text-[11px] text-slate-400">扫码互联·微信战绩永久同步</p>
                    </div>
                  </div>

                  {/* QR Code Frame */}
                  <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 flex flex-col items-center text-center space-y-2">
                    <div className="p-1.5 bg-white rounded-xl shadow-lg">
                      <img
                        src="/assets/wechat-qr.jpg"
                        alt="唯爱掼蛋微信互联"
                        className="w-48 h-48 object-cover rounded-lg"
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 leading-tight">
                      微信扫一扫关注唯爱服务号 / 加入官方校友牌友群
                    </p>
                  </div>

                  {/* Input WeChat ID */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-slate-300 block">
                      绑定您的微信昵称或微信号：
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="例如：微信名 或 微信号..."
                        value={wechatNicknameInput}
                        onChange={(e) => setWechatNicknameInput(e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                      />
                      <button
                        disabled={isBindingWechat}
                        onClick={handleBindWechat}
                        className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow transition-transform active:scale-95 disabled:opacity-50 shrink-0"
                      >
                        {isBindingWechat ? '绑定中...' : '确认关联'}
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-slate-800/80 pt-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowWechatModal(false);
                        handleRealLogin();
                      }}
                      className="w-full py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all flex items-center justify-center gap-1"
                    >
                      <span>前往微信开放平台 OAuth 授权登录</span>
                      <span>↗</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
